import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import NegotiationState from '../../../_shared/constants/negotiationState';
import {
  TradeContext,
  TradeState,
  TradeStateNew
} from '../../../_shared/types/trade';

import NegotiationModalContainer from '../../Modal/NegotiationModalContainer';
import NowInNewState from '../PendingTradeCardContent/PendingDecisionFooter/NowInNewState';
import TutorialNegotiationModal from '../../Modal/TutorialNegotiationModal';
import NegotiationErrorModalContainer from '../../ModalButton/NegotiationButton/NegotiationErrorModalContainer';
import NegotiationService from '../../../services/NegotiationService';

import {
  BidInfoTitle,
  BidInfoValue,
  BlueDot,
  Column,
  ExternalContainer,
  LeftContainer,
  NegotiationEndReason,
  StyledPrimaryButton,
  Row,
  StyledSecondaryButton
} from './Styles';
import UserService from '../../../services/UserService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import useNegotiationOfferLabels from '../../../_shared/hooks/trade/useNegotiationOfferLabels';
import useNegotiationTutorialVariant from '../../../_shared/hooks/trade/useNegotiationTutorialVariant';
import NowInNewStateVariants from '../../../_shared/constants/nowInNewStateVariants';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';

const NegotiationCardFooter = ({
  trade,
  testId,
  tradeContext,
  disableActionButtons,
  isOmp = false,
  handleNavigateToTradeUri
}) => {
  const track = useAnalytics();
  const {
    sellerNegotiated,
    negotiationState,
    topBid,
    id: tradeId,
    auctionId,
    negotiationOffer,
    isGreyOut,
    tradeState: currentState,
    associatedTradeId,
    isInternalTrade,
    sellingNetworkId
  } = trade;
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;
  const userService = UserService.get();
  const user = useSelector(state => state?.auth?.user, shallowEqual);
  const isCsu = user?.superUserId && user?.superUserId !== user?.id;
  const [
    offerTitle,
    actionRequired,
    highlightBidValue,
    highlightBidLabel
  ] = useNegotiationOfferLabels(negotiationState, tradeContext, isGreyOut);
  const [hasTutorial, tutorialVariant] = useNegotiationTutorialVariant(
    tradeContext,
    negotiationState,
    sellingNetworkId,
    associatedTradeId,
    isInternalTrade
  );

  const [
    tutorialNegotiationModalIsOpen,
    setTutorialNegotiationModalIsOpen
  ] = useState(false);

  const [negotiationModalIsOpen, setNegotiationModalIsOpen] = useState(false);

  const [
    isNegotiationErrorModalOpen,
    setIsNegotiationErrorModalOpen
  ] = useState(false);

  const [negotiationErrorMessage, setNegotiationErrorMessage] = useState(null);

  const isBuyContext = tradeContext === TradeContext.Buy;
  const isSellContext = tradeContext === TradeContext.Sell;

  const canSendToClosing = !isGreyOut && isSellContext && !sellerNegotiated;
  const sentToSecondBidder =
    isGreyOut &&
    isBuyContext &&
    negotiationState === NegotiationState.FINAL_ASKING_PRICE;
  const nowInActive =
    isGreyOut && isBuyContext && currentState === TradeState.active;
  const nowInUpcoming =
    isGreyOut && isBuyContext && currentState === TradeStateNew.UPCOMING;
  const isCurrentUserSecondBidder =
    !isGreyOut &&
    isBuyContext &&
    negotiationState === NegotiationState.FINAL_ASKING_PRICE;

  const getUserType = () => {
    if (userService.isSeller(user, trade?.dealer?.id)) {
      return MixpanelConfig.userType.seller;
    }

    if (isCurrentUserSecondBidder) {
      return MixpanelConfig.userType.secondBidder;
    }

    return MixpanelConfig.userType.buyer;
  };

  const showView = () => {
    if (isGreyOut) {
      // Redact the negotiation history for Buyer(s) but available for CSUs and Seller.
      return (isBuyContext && isCsu) || isSellContext;
    }

    switch (tradeContext) {
      case TradeContext.Buy:
        return [
          NegotiationState.BUYER_OFFER,
          NegotiationState.BUYER_COUNTER,
          NegotiationState.BUYER_FIRM,
          NegotiationState.DECLINED
        ].includes(negotiationState);
      case TradeContext.Sell:
        return [
          NegotiationState.SELLER_OFFER,
          NegotiationState.SELLER_COUNTER,
          NegotiationState.DECLINED,
          NegotiationState.FINAL_ASKING_PRICE
        ].includes(negotiationState);
      default:
        return false;
    }
  };

  const showReply = () => {
    switch (tradeContext) {
      case TradeContext.Buy:
        return (
          !isGreyOut &&
          [
            NegotiationState.SELLER_OFFER,
            NegotiationState.SELLER_COUNTER,
            NegotiationState.FINAL_ASKING_PRICE
          ].includes(negotiationState)
        );
      case TradeContext.Sell:
        return (
          !isGreyOut &&
          [
            NegotiationState.BUYER_OFFER,
            NegotiationState.BUYER_COUNTER,
            NegotiationState.BUYER_FIRM
          ].includes(negotiationState)
        );
      default:
        return false;
    }
  };

  const onViewNegotiationModal = () => {
    setNegotiationModalIsOpen(true);

    track(SegmentConfig.names.pendingViewButtonClicked, {
      ...getVinBasicProperties(trade),
      ...getSellerProperties(trade),
      event_state_type: userService.isSeller(user, trade?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });
    track(MixpanelConfig.names.viewNegotiationHistory, {
      user_type: getUserType(),
      source: eventSource,
      trade_id: tradeId,
      auction_id: auctionId
    });
  };

  const onReplyNegotiationModal = () => {
    if (hasTutorial) {
      const negotiationService = NegotiationService.get();
      negotiationService.getNegotiationMessage(
        associatedTradeId,
        tradeContext,
        setNegotiationErrorMessage,
        setIsNegotiationErrorModalOpen,
        setTutorialNegotiationModalIsOpen
      );
    } else setNegotiationModalIsOpen(true);
    track(SegmentConfig.names.pendingReplyClicked, {
      ...getVinBasicProperties(trade),
      ...getSellerProperties(trade),
      event_state_type: userService.isSeller(user, trade?.dealer?.id)
        ? SegmentConfig.eventStateType.sellPending
        : SegmentConfig.eventStateType.buyPending
    });
    track(MixpanelConfig.names.replyToOffer, {
      user_type: getUserType(),
      source: eventSource,
      trade_id: tradeId,
      auction_id: auctionId
    });
  };

  const onCloseNegotiationModal = () => {
    setNegotiationModalIsOpen(false);
  };

  const getReplyButton = () => {
    const isReplyTheOnlyButton =
      (isBuyContext && showReply()) || (isSellContext && !canSendToClosing);
    const replyText = <FormattedMessage id="list.card.negotiation.reply" />;
    return isReplyTheOnlyButton ? (
      <StyledPrimaryButton
        onClick={onReplyNegotiationModal}
        disabled={disableActionButtons}
      >
        {replyText}
      </StyledPrimaryButton>
    ) : (
      <StyledSecondaryButton
        onClick={onReplyNegotiationModal}
        disabled={disableActionButtons}
      >
        {replyText}
      </StyledSecondaryButton>
    );
  };

  return (
    <ExternalContainer data-testid={testId}>
      <Row>
        <LeftContainer>
          <Row>
            <Column>
              <BidInfoTitle>
                <FormattedMessage
                  id={
                    isSellContext
                      ? 'list.card.negotiation.topBid'
                      : 'list.card.negotiation.yourTopBid'
                  }
                />
              </BidInfoTitle>
              <BidInfoValue>{convertToCurrency(topBid)}</BidInfoValue>
            </Column>
            <Column>
              <BidInfoTitle highlightBidLabel={highlightBidLabel}>
                {offerTitle}
              </BidInfoTitle>
              <BidInfoValue highlightBidValue={highlightBidValue}>
                {actionRequired && <BlueDot />}
                {convertToCurrency(negotiationOffer)}
              </BidInfoValue>
            </Column>
            {nowInActive && (
              <Column>
                <NegotiationEndReason>
                  <NowInNewState
                    tradeId={tradeId}
                    variant={NowInNewStateVariants.IN_ACTIVE}
                    tradeContext={tradeContext}
                    handleNavigateToTradeUri={handleNavigateToTradeUri}
                    isOmp={isOmp}
                  />
                </NegotiationEndReason>
              </Column>
            )}
            {nowInUpcoming && (
              <Column>
                <NegotiationEndReason>
                  <NowInNewState
                    tradeId={tradeId}
                    variant={NowInNewStateVariants.IN_UPCOMING}
                    tradeContext={tradeContext}
                    handleNavigateToTradeUri={handleNavigateToTradeUri}
                    isOmp={isOmp}
                  />
                </NegotiationEndReason>
              </Column>
            )}
            {sentToSecondBidder && (
              <Column>
                <NegotiationEndReason>
                  <FormattedMessage id="vdp.pending.buyer.label.sentToSecondBidder" />
                </NegotiationEndReason>
              </Column>
            )}
          </Row>
        </LeftContainer>
        <Row>
          {showView() && (
            <StyledSecondaryButton onClick={onViewNegotiationModal}>
              <FormattedMessage id="list.card.negotiation.view" />
            </StyledSecondaryButton>
          )}
          {showReply() && getReplyButton()}
        </Row>
      </Row>

      {hasTutorial && (
        <>
          {isNegotiationErrorModalOpen ? (
            <NegotiationErrorModalContainer
              isNegotiationErrorModalOpen={isNegotiationErrorModalOpen}
              negotiationErrorMessage={negotiationErrorMessage}
              setIsNegotiationErrorModalOpen={setIsNegotiationErrorModalOpen}
              tradeId={tradeId}
              auctionId={auctionId}
            />
          ) : (
            <TutorialNegotiationModal
              modalIsOpen={tutorialNegotiationModalIsOpen}
              onClose={() => {
                setTutorialNegotiationModalIsOpen(false);
              }}
              onStartNegotiation={() => {
                setTutorialNegotiationModalIsOpen(false);
                setNegotiationModalIsOpen(true);
              }}
              variant={tutorialVariant}
            />
          )}
        </>
      )}
      <NegotiationModalContainer
        trade={trade}
        modalIsOpen={negotiationModalIsOpen}
        tradeContext={tradeContext}
        onClose={onCloseNegotiationModal}
        isOmp={isOmp}
      />
    </ExternalContainer>
  );
};

export default NegotiationCardFooter;
